// src/services/serviceWorkerUpdate.ts

import { message, Modal } from 'antd';

/**
 * Gestiona las actualizaciones del Service Worker
 * Debe llamarse una vez en el componente principal de la aplicación
 */
export const setupServiceWorkerUpdates = () => {
  if (!('serviceWorker' in navigator)) {
    return; // Service Worker no soportado
  }
  
  let refreshing = false;
  
  // Detectar cuando el Service Worker controla la página
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    console.log('Service Worker controller changed, reloading page...');
    window.location.reload();
  });
  
  // Escuchar mensajes del Service Worker
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'NEW_VERSION_AVAILABLE') {
      console.log('Nueva versión disponible:', event.data.version);
      showUpdateNotification(event.data.version);
    }
    
    if (event.data && event.data.type === 'RELOAD_PAGE') {
      console.log('Recargando página por petición del Service Worker');
      window.location.reload();
    }
  });
  
  // Verificar actualizaciones cada 30 minutos (puedes ajustar este tiempo)
  setInterval(checkForUpdates, 30 * 60 * 1000);
  
  // Comprobar actualizaciones al inicio
  setTimeout(checkForUpdates, 5000);
};

/**
 * Comprobar si hay actualizaciones del Service Worker
 */
export const checkForUpdates = async () => {
  if (!('serviceWorker' in navigator)) return;
  
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return;
    
    console.log('Verificando actualizaciones del Service Worker...');
    await registration.update();
  } catch (error) {
    console.error('Error al comprobar actualizaciones:', error);
  }
};

/**
 * Aplicar la actualización inmediatamente
 */
export const applyUpdate = async () => {
  if (!('serviceWorker' in navigator)) return false;
  
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration || !registration.waiting) return false;
    
    // Enviar mensaje para activar el service worker en espera
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    
    // También podemos forzar recarga inmediata
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'FORCE_REFRESH' });
    }
    
    return true;
  } catch (error) {
    console.error('Error al aplicar actualización:', error);
    return false;
  }
};

/**
 * Mostrar notificación de actualización al usuario
 */
const showUpdateNotification = (version: string) => {
  Modal.confirm({
    title: 'Nueva versión disponible',
    content: `Hay una nueva versión (${version}) de la aplicación disponible. ¿Deseas actualizar ahora?`,
    okText: 'Actualizar ahora',
    cancelText: 'Más tarde',
    onOk: async () => {
      message.loading('Aplicando actualización...');
      const success = await applyUpdate();
      if (!success) {
        message.info('Para completar la actualización, por favor recarga la página.');
        // Dar al usuario la opción de recargar manualmente
        setTimeout(() => {
          Modal.confirm({
            title: 'Actualización pendiente',
            content: 'Para completar la actualización, es necesario recargar la página. ¿Quieres hacerlo ahora?',
            okText: 'Recargar ahora',
            cancelText: 'Más tarde',
            onOk: () => window.location.reload()
          });
        }, 2000);
      }
    }
  });
};

/**
 * Forzar actualización y recarga
 * Útil para botón de actualización manual
 */
export const forceUpdateAndReload = async () => {
  await checkForUpdates();
  await applyUpdate();
  setTimeout(() => window.location.reload(), 1000);
};

export default {
  setupServiceWorkerUpdates,
  checkForUpdates,
  applyUpdate,
  forceUpdateAndReload
};